// src/components/FlappyBirdLobby.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import FlappyBirdGame from './FlappyBirdGame';

const FlappyBirdLobby = ({ token }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'solo', 'multi'
  const [status, setStatus] = useState('idle');
  const [roomId, setRoomId] = useState(null);
  const [opponentId, setOpponentId] = useState(null);
  const [startDelay, setStartDelay] = useState(0);
  const [mapData, setMapData] = useState(null);
  const socketRef = useRef(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('memo_user');
    if (userStr) setUser(JSON.parse(userStr));
  }, []);

  useEffect(() => {
    if (!token) return;
    const socket = io('/', { query: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('flappy_game_start', (data) => {
      setRoomId(data.room_id);
      setOpponentId(data.opponent_id);
      setStartDelay(3);
      setMapData(data.map_data); // 服务器下发的完整管道数组
      setStatus('playing');
    });

    // 结果由游戏组件处理，此处不再需要 alert
    return () => socket.disconnect();
  }, [token]);

  const startMatch = () => {
    setStatus('matching');
    socketRef.current.emit('flappy_match_start', { token });
  };

  const cancelMatch = () => {
    socketRef.current.emit('flappy_cancel_match', { token });
    setStatus('idle');
  };

  const goBack = () => navigate('/game-center');

  if (status === 'playing') {
    return (
      <FlappyBirdGame
        mode="multi"
        roomId={roomId}
        socket={socketRef.current}
        opponentId={opponentId}
        userId={user?.id}
        mapData={mapData}
        onGameEnd={() => setStatus('idle')}
      />
    );
  }
  if (mode === 'solo') {
    return <FlappyBirdGame mode="solo" onGameEnd={() => setMode(null)} />;
  }

  // 匹配大厅 UI
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      padding: '20px',
    },
    background: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #f1c40f, #e67e22)',
      zIndex: 0,
    },
    card: {
      position: 'relative',
      zIndex: 1,
      backgroundColor: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '30px',
      padding: '40px 30px',
      width: '100%',
      maxWidth: '500px',
      textAlign: 'center',
      boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
      border: '1px solid rgba(255,255,255,0.3)',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#2c3e50',
      marginBottom: '20px',
    },
    modeButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      marginBottom: '30px',
    },
    soloButton: {
      background: '#3498db',
      color: 'white',
      border: 'none',
      padding: '14px',
      fontSize: '18px',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'transform 0.2s',
      boxShadow: '0 4px 0 #1f6392',
    },
    multiButton: {
      background: '#e67e22',
      color: 'white',
      border: 'none',
      padding: '14px',
      fontSize: '18px',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'transform 0.2s',
      boxShadow: '0 4px 0 #b45f1b',
    },
    cancelButton: {
      background: '#e74c3c',
      color: 'white',
      border: 'none',
      padding: '14px',
      fontSize: '18px',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: 'bold',
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#e67e22',
      fontSize: '16px',
      cursor: 'pointer',
      textDecoration: 'underline',
      marginTop: '20px',
    },
    matchingContainer: {
      textAlign: 'center',
    },
    spinner: {
      width: '50px',
      height: '50px',
      border: '4px solid #e0e0e0',
      borderTopColor: '#e67e22',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px',
    },
    matchingText: {
      fontSize: '18px',
      color: '#2c3e50',
      marginBottom: '20px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.background} />
      <div style={styles.card}>
        <h2 style={styles.title}>🐦 Flappy Bird 对决</h2>
        <div style={styles.modeButtons}>
          <button onClick={() => setMode('solo')} style={styles.soloButton}>✈️ 单人模式</button>
          {status === 'matching' ? (
            <div style={styles.matchingContainer}>
              <div style={styles.spinner} />
              <p style={styles.matchingText}>正在匹配对手...</p>
              <button onClick={cancelMatch} style={styles.cancelButton}>取消匹配</button>
            </div>
          ) : (
            <button onClick={startMatch} style={styles.multiButton}>🌍 联机对战</button>
          )}
        </div>
        <button onClick={goBack} style={styles.backButton}>← 返回游戏中心</button>
      </div>
    </div>
  );
};

export default FlappyBirdLobby;